import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Auth() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refParam = params.get("ref") || "";
  const [tab, setTab] = useState(refParam ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "", ref: refParam });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === "login") {
        await login(form.email, form.password);
        toast.success("¡Bienvenido de nuevo! 👋");
      } else {
        await register({ name: form.name, email: form.email, password: form.password, ref: form.ref || null });
        toast.success("¡Cuenta creada! 🎉");
      }
      nav("/cuenta");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <div className="bg-white rounded-2xl border shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-drelo-primary to-drelo-deal text-white p-6 text-center">
          <Sparkles className="w-8 h-8 mx-auto mb-2" />
          <h1 className="font-display font-black text-2xl uppercase">{tab === "login" ? "Bienvenido" : "Únete a Drelo"}</h1>
          <p className="text-sm text-white/90 mt-1">Recompensas, ofertas exclusivas y más.</p>
        </div>

        <div className="flex border-b">
          {["login", "register"].map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`flex-1 py-3 font-bold text-sm uppercase ${tab === t ? "text-drelo-primary border-b-2 border-drelo-primary" : "text-drelo-muted"}`} data-testid={`tab-${t}`}>
              {t === "login" ? "Entrar" : "Crear cuenta"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="p-6 space-y-3">
          {tab === "register" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre completo" className="w-full border rounded-lg px-3 py-3 text-sm outline-none focus:border-drelo-primary" data-testid="auth-name" />
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full border rounded-lg px-3 py-3 text-sm outline-none focus:border-drelo-primary" data-testid="auth-email" />
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Contraseña" className="w-full border rounded-lg px-3 py-3 text-sm outline-none focus:border-drelo-primary" data-testid="auth-password" />
          {tab === "register" && (
            <div className="flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-lg px-3">
              <Gift className="w-4 h-4 text-drelo-primary" />
              <input value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value.toUpperCase() })} placeholder="Código de referido (opcional)" className="w-full bg-transparent py-3 text-sm outline-none uppercase" data-testid="auth-ref" />
            </div>
          )}
          <button disabled={loading} className="w-full bg-drelo-primary text-white py-3.5 rounded-xl font-black uppercase tracking-wide hover:bg-drelo-hover active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60" data-testid="auth-submit">
            {loading && <Loader2 className="w-5 h-5 animate-spin" />}
            {tab === "login" ? "Entrar" : "Crear mi cuenta"}
          </button>
        </form>
      </div>
      <p className="text-center text-sm text-drelo-muted mt-4">¿Solo quieres comprar? <Link to="/tienda" className="text-drelo-primary font-bold">Compra como invitado</Link></p>
    </div>
  );
}
